import React, { useEffect, useId, useState } from "react";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { Label } from "@/components/_atoms/label";
import { Switch } from "@/components/_atoms/switch";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { capitalizeFirstLetter } from "@/lib/capitalizeFirstLetter";

import { useCreateStoredView } from "../../hooks/api/use-create-stored-view";
import { useUpdateActiveView } from "../../hooks/api/use-update-active-view";
import { useCurrentViewContext } from "../../hooks/use-current-view-context";
import { StoredView } from "../../models/stored-view-model";
import { TeamStoredViewInput, WorkspaceStoredViewInput } from "../../models/stored-view-type";

interface StoredViewCreateFormProps {
  initialData: Omit<StoredView, "id" | "createdAt" | "updatedAt" | "ownerId" | "ownerType">;
  user: {
    id: string;
  };
  canBeShared?: TeamStoredViewInput | WorkspaceStoredViewInput | null | undefined;
  onError?: (error: Error) => void;
  onSuccess?: (data: StoredView) => void;
  onCancel?: () => void;
}

export const StoredViewCreateForm: React.FC<StoredViewCreateFormProps> = ({
  user,
  initialData,
  canBeShared,
  onCancel,
  ...props
}) => {
  const { t } = useDomainTranslation("common");
  const [name, setName] = useState(initialData.name);
  const [isDefault, setIsDefault] = useState(initialData.default);
  const [isShared, setIsShared] = useState(false);
  const sharedSwitchId = useId();
  const defaultSwitchId = useId();

  const { viewKey, workspaceId, currentDatatableState, currentActiveView } = useCurrentViewContext();
  const { createStoredView, loading: createStoredViewLoading } = useCreateStoredView();
  const { updateActiveView, loading: updateActiveViewLoading } = useUpdateActiveView();

  useEffect(() => {
    setName(initialData.name);
    setIsDefault(initialData.default);
    setIsShared(false);
  }, [initialData]);

  const handleCreateSavedView = async (e: React.FormEvent) => {
    e.preventDefault();

    if (createStoredViewLoading || updateActiveViewLoading) {
      return;
    }

    const ownerConfig =
      isShared && canBeShared
        ? {
            ownerType: canBeShared.ownerType,
            ownerId: canBeShared.ownerId,
          }
        : {
            ownerType: "USER",
            ownerId: user.id,
          };

    createStoredView(
      {
        workspaceId: workspaceId,
        key: viewKey,
        name,
        default: isDefault,
        ...ownerConfig,
        columnFilters: currentDatatableState.columnFilters,
        columnOrder: currentDatatableState.columnOrder,
        columnVisibility: currentDatatableState.columnVisibility,
        sorting: currentDatatableState.sorting,
        showColumnHeaders: currentDatatableState.showColumnHeaders,
        globalFilter: currentDatatableState.globalFilter,
        expanded: currentDatatableState.expanded,
        sectionColumnId: currentDatatableState.sectionColumnId,
        subSectionColumnId: currentDatatableState.subSectionColumnId,
      },
      {
        onSuccess: (newStoredView) => {
          if (currentActiveView) {
            updateActiveView({
              id: currentActiveView.id,
              storedViewId: newStoredView.id,
            });
          }
          props.onSuccess?.(newStoredView);
        },
        onError: (error) => {
          props.onError?.(error);
        },
      },
    );
  };

  return (
    <form onSubmit={handleCreateSavedView} className="space-y-4 p-4">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium">
          {t("fields.name")}
        </Label>
        <Input
          id="name"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          className="w-full"
          placeholder={t("view.namePlaceholder")}
        />
      </div>

      {canBeShared && (
        <div className="flex items-center gap-2">
          <Switch
            id={sharedSwitchId}
            size="sm"
            checked={isShared}
            onCheckedChange={setIsShared}
            className="relative cursor-pointer rounded-full bg-muted transition-colors data-[state=checked]:bg-primary"
          ></Switch>
          <Label htmlFor={sharedSwitchId} className="text-sm font-medium">{t("view.shareWithOwner", { ownerType: capitalizeFirstLetter(canBeShared.ownerType) })}</Label>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Switch
          id={defaultSwitchId}
          size="sm"
          checked={isDefault}
          onCheckedChange={setIsDefault}
          className="relative cursor-pointer rounded-full bg-muted transition-colors data-[state=checked]:bg-primary"
        ></Switch>
        <Label htmlFor={defaultSwitchId} className="text-sm font-medium">{t("view.setAsDefault")}</Label>
      </div>

      <div className="border-t pt-4">
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" size="xs" onClick={onCancel} className="px-4">
              {t("view.cancel")}
            </Button>
          )}
          <Button
            type="submit"
            size="xs"
            disabled={!name || createStoredViewLoading || updateActiveViewLoading}
            className="px-4"
          >
            {createStoredViewLoading || updateActiveViewLoading ? t("view.saving") : t("view.save")}
          </Button>
        </div>
      </div>
    </form>
  );
};
